import React, { useEffect } from 'react';

import { isOptionSelected } from '../../helpers/navigationMenuHelpers';
import { OptionType } from '../../NavigationMenu';
import { NavigationMenuOption } from '../navigation-menu-option/NavigationMenuOption';
import { NavigationMenuSearch } from '../navigation-menu-search/NavigationMenuSearch';

import './NavigationMenuDropdown.scss';

interface NavigationMenuDropdownProps {
	listboxId: string;
	searchId: string;
	ariaLabel: string;
	searchTerm: string;
	filteredOptions: OptionType[];
	focusedIndex: number;
	selectedValue: OptionType;
	onSearchChange: (value: string) => void;
	onOptionSelect: (option: OptionType) => void;
	onFocusedIndexChange: (index: number) => void;
	onKeyDown: (event: React.KeyboardEvent) => void;
	searchInputRef: React.RefObject<HTMLInputElement>;
	listboxRef: React.RefObject<HTMLUListElement>;
}

export const NavigationMenuDropdown: React.FC<NavigationMenuDropdownProps> = ({
	listboxId,
	searchId,
	ariaLabel,
	searchTerm,
	filteredOptions,
	focusedIndex,
	selectedValue,
	onSearchChange,
	onOptionSelect,
	onFocusedIndexChange,
	onKeyDown,
	searchInputRef,
	listboxRef
}) => {
	// Get the active descendant ID for the focused option
	const getActiveDescendantId = (): string | undefined => {
		if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
			return `${listboxId}-option-${focusedIndex}`;
		}
		return undefined;
	};

	// Create announcement text for screen readers when options are filtered
	const getOptionsAnnouncementText = (): string => {
		if (filteredOptions.length === 0) {
			return 'No business units found';
		}
		if (searchTerm.trim()) {
			return `${filteredOptions.length} business units found`;
		}
		return `${filteredOptions.length} business units available`;
	};

	// Scroll focused option into view when navigating with keyboard
	useEffect(() => {
		if (focusedIndex >= 0 && focusedIndex < filteredOptions.length) {
			listboxRef.current
				?.querySelector(`#${listboxId}-option-${focusedIndex}`)
				?.scrollIntoView({ block: 'nearest', inline: 'nearest' });
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [focusedIndex, filteredOptions.length, listboxId]);

	return (
		<div className="navigation-menu__dropdown">
			<NavigationMenuSearch
				searchId={searchId}
				searchTerm={searchTerm}
				onSearchChange={onSearchChange}
				onKeyDown={onKeyDown}
				searchInputRef={searchInputRef}
				activedescendant={getActiveDescendantId()}
			/>

			{/* Instructions for screen readers */}
			<div className="sr-only" id={`${listboxId}-instructions`}>
				Selecting a business unit will navigate you to its dashboard
			</div>

			{/* Live region for announcing search results */}
			<div aria-live="polite" aria-atomic="true" className="sr-only" id={`${listboxId}-status`}>
				{getOptionsAnnouncementText()}
			</div>

			<ul
				ref={listboxRef}
				id={listboxId}
				role="listbox"
				className="navigation-menu__listbox"
				aria-label={`${ariaLabel}, ${getOptionsAnnouncementText()}`}
				aria-describedby={`${listboxId}-instructions ${listboxId}-status`}
				aria-activedescendant={getActiveDescendantId()}
				tabIndex={0}
				onKeyDown={onKeyDown}
			>
				{filteredOptions.length === 0 ? (
					<li
						role="presentation"
						className="navigation-menu__option navigation-menu__option--no-results"
					>
						No options found
					</li>
				) : (
					filteredOptions.map((option, index) => (
						<NavigationMenuOption
							key={option.value}
							option={option}
							index={index}
							isSelected={isOptionSelected(option, selectedValue)}
							isFocused={index === focusedIndex}
							onSelect={onOptionSelect}
							onMouseEnter={onFocusedIndexChange}
							listboxId={listboxId}
						/>
					))
				)}
			</ul>
		</div>
	);
};
